
import api from '../utils/api';
import { apiCache } from '../utils/apiCache';

// Утилита для инвалидации кэша конкретной кампании
const invalidateCampaignCache = (campaignId) => {
  apiCache.set(`campaign-${campaignId}`, null);
  apiCache.set(`campaign-instances-${campaignId}`, null);
  apiCache.set(`campaign-contacts-${campaignId}`, null);
  apiCache.set(`campaign-full-${campaignId}`, null);
  // Также очищаем общий список кампаний
  apiCache.set('campaigns-all', null);
  apiCache.set('campaigns-', null);
};

// Get all campaigns с кэшированием
export const getAllCampaigns = async (status = '') => {
  const cacheKey = `campaigns-${status}`;
  const cached = apiCache.get(cacheKey);

  if (cached) {
    console.log('Cache hit for campaigns:', cacheKey);
    return cached;
  }

  try {
    const response = await api.get(`/campaigns${status ? `?status=${status}` : ''}`);
    apiCache.set(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

// Get campaign by ID с кэшированием
export const getCampaignById = async (id) => {
  const cacheKey = `campaign-${id}`;
  const cached = apiCache.get(cacheKey);

  if (cached) {
    console.log('Cache hit for campaign:', id);
    return cached;
  }

  try {
    const response = await api.get(`/campaigns/${id}`);
    apiCache.set(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching campaign ${id}:`, error);
    throw error;
  }
};

// НОВАЯ ФУНКЦИЯ: Get campaign with all related data в одном запросе
export const getCampaignFull = async (id) => {
  const cacheKey = `campaign-full-${id}`;
  const cached = apiCache.get(cacheKey);

  if (cached) {
    console.log('Cache hit for full campaign:', id);
    return cached;
  }

  try {
    const response = await api.get(`/campaigns/${id}/full`);
    apiCache.set(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching full campaign data for ${id}:`, error);
    throw error;
  }
};

// Get campaign instances с кэшированием
export const getCampaignInstances = async (id) => {
  const cacheKey = `campaign-instances-${id}`;
  const cached = apiCache.get(cacheKey);

  if (cached) {
    console.log('Cache hit for campaign instances:', id);
    return cached;
  }

  try {
    const response = await api.get(`/campaigns/${id}/instances`);
    apiCache.set(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching instances for campaign ${id}:`, error);
    throw error;
  }
};

// Get campaign contacts с кэшированием
export const getCampaignContacts = async (id) => {
  const cacheKey = `campaign-contacts-${id}`;
  const cached = apiCache.get(cacheKey);

  if (cached) {
    console.log('Cache hit for campaign contacts:', id);
    return cached;
  }

  try {
    const response = await api.get(`/campaigns/${id}/contacts`);
    apiCache.set(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching contacts for campaign ${id}:`, error);
    throw error;
  }
};

// Update campaign с инвалидацией кэша
export const updateCampaign = async (id, campaignData) => {
  try {
    const response = await api.put(`/campaigns/${id}`, campaignData);
    invalidateCampaignCache(id);
    return response.data;
  } catch (error) {
    console.error(`Error updating campaign ${id}:`, error);
    throw error;
  }
};

// Start campaign с инвалидацией кэша
export const startCampaign = async (id) => {
  try {
    const response = await api.post(`/campaigns/${id}/start`);
    invalidateCampaignCache(id);
    return response.data;
  } catch (error) {
    console.error(`Error starting campaign ${id}:`, error);
    throw error;
  }
};

// Pause campaign с инвалидацией кэша
export const pauseCampaign = async (id) => {
  try {
    const response = await api.post(`/campaigns/${id}/pause`);
    invalidateCampaignCache(id);
    return response.data;
  } catch (error) {
    console.error(`Error pausing campaign ${id}:`, error);
    throw error;
  }
};

// Stop campaign с инвалидацией кэша
export const stopCampaign = async (id) => {
  try {
    const response = await api.post(`/campaigns/${id}/stop`);
    invalidateCampaignCache(id);
    return response.data;
  } catch (error) {
    console.error(`Error stopping campaign ${id}:`, error);
    throw error;
  }
};

// Create campaign с инвалидацией списка кампаний
export const createCampaign = async (campaignData) => {
  try {
    const response = await api.post('/campaigns', campaignData);
    // Очищаем кэш списка кампаний
    apiCache.set('campaigns-all', null);
    apiCache.set('campaigns-', null);
    return response.data;
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
};

// Delete campaign с инвалидацией кэша
export const deleteCampaign = async (id) => {
  try {
    const response = await api.delete(`/campaigns/${id}`);
    invalidateCampaignCache(id);
    return response.data;
  } catch (error) {
    console.error(`Error deleting campaign ${id}:`, error);
    throw error;
  }
};

// Add instances с инвалидацией кэша
export const addInstancesToCampaign = async (id, instanceIds) => {
  try {
    if (id >= 0) {
      console.log('Adding instances to campaign', instanceIds);
      const response = await api.post(`/campaigns/${id}/instances`, {
        instance_ids: instanceIds
      });
      invalidateCampaignCache(id);
      return response.data;
    } else {
      return {
        message: 'Invalid campaign ID'
      }
    }
  } catch (error) {
    console.error(`Error adding instances to campaign ${id}:`, error);
    throw error;
  }
};

// Remove instances с инвалидацией кэша
export const removeInstancesFromCampaign = async (id, instanceIds) => {
  try {
    console.log('Removing instances from campaign', instanceIds);
    const response = await api.delete(`/campaigns/${id}/instances`, {
      data: { instance_ids: instanceIds }
    });
    invalidateCampaignCache(id);
    return response.data;
  } catch (error) {
    console.error(`Error removing instances from campaign ${id}:`, error);
    throw error;
  }
};

// Add contacts с инвалидацией кэша
export const addContactsToCampaign = async (id, contactIds) => {
  try {
    const response = await api.post(`/campaigns/${id}/contacts`, { contact_ids: contactIds });
    invalidateCampaignCache(id);
    return response.data;
  } catch (error) {
    console.error(`Error adding contacts to campaign ${id}:`, error);
    throw error;
  }
};

// Remove contacts с инвалидацией кэша
export const removeContactsFromCampaign = async (id, contactIds) => {
  try {
    const response = await api.delete(`/campaigns/${id}/contacts`, {
      data: { contact_ids: contactIds }
    });
    invalidateCampaignCache(id);
    return response.data;
  } catch (error) {
    console.error(`Error removing contacts from campaign ${id}:`, error);
    throw error;
  }
};

// Send test message (does not affect stats)
export const sendCampaignTest = async (id, phone) => {
  try {
    const response = await api.post(`/campaigns/${id}/test-send`, { phone });
    return response.data;
  } catch (error) {
    console.error(`Error sending test message for campaign ${id}:`, error);
    throw error;
  }
};

// Copy campaign с инвалидацией списка
export const copyCampaign = async (id) => {
  try {
    const response = await api.post(`/campaigns/${id}/copy`);
    // Очищаем кэш списка кампаний
    apiCache.set('campaigns-all', null);
    apiCache.set('campaigns-', null);
    return response.data;
  } catch (error) {
    console.error(`Error copying campaign ${id}:`, error);
    throw error;
  }
};

export const getCampaignShareLink = async (id, expiresIn = null) => {
  try {
    const response = await api.post(`/campaigns/${id}/share`, expiresIn ? { expiresIn } : {});
    return response.data; // { token, url, expiresIn }
  } catch (error) {
    console.error(`Error generating share link for campaign ${id}:`, error);
    throw error;
  }
};

export const getCampaignStats = async (id) => {
  try {
    const response = await api.get(`/campaigns/${id}/stats`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching stats for campaign ${id}:`, error);
    throw error;
  }
};

// Остальные функции остаются без изменений
export const getCampaignHistory = async (id) => {
  try {
    const response = await api.get(`/campaigns/${id}/history`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching history for campaign ${id}:`, error);
    throw error;
  }
};

export const getCampaignContactStatuses = async (id) => {
  try {
    const response = await api.get(`/campaigns/${id}/contact-statuses`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching contact statuses for campaign ${id}:`, error);
    throw error;
  }
};

export const updateCampaignContacts = async (id, newContactIds) => {
  try {
    const currentContacts = await getCampaignContacts(id);
    const currentIds = currentContacts.map((c) => c.id);

    const toAdd = newContactIds.filter((id) => !currentIds.includes(id));
    const toRemove = currentIds.filter((id) => !newContactIds.includes(id));

    if (toAdd.length > 0) {
      await addContactsToCampaign(id, toAdd);
    }
    if (toRemove.length > 0) {
      await removeContactsFromCampaign(id, toRemove);
    }

    return { added: toAdd, removed: toRemove };
  } catch (error) {
    console.error(`Error updating contacts for campaign ${id}:`, error);
    throw error;
  }
};